import type { ServiceFrom, StateMachine } from '@xstate/fsm'
import { assign, createMachine } from '@xstate/fsm'

import type { IGlobalState } from '@core/store'
import {
  getGlobalStore,
  persistedDataSelector,
  sharedStateActionsSelector,
} from '@core/store'
import { isMobileWeb } from '@core/userAgent'

export type WidgetState = 'minimized' | 'chat' | 'fullscreen'

interface PersistedUIContext {
  widgetState: WidgetState
  prevWidgetState: WidgetState
}

export const initialPersistedUIContext: PersistedUIContext = {
  widgetState: 'minimized',
  prevWidgetState: 'minimized',
}

let persistedUIContextCache: PersistedUIContext | undefined

/**
 * Parses the UI context from the persisted data.
 */
export function parseUIContext(uiContextStr?: string) {
  return (
    uiContextStr ? JSON.parse(uiContextStr) : initialPersistedUIContext
  ) as PersistedUIContext
}

/**
 * Returns the persisted UI context
 */
export function getLiveHelperUIContext(): PersistedUIContext {
  try {
    if (!persistedUIContextCache) {
      persistedUIContextCache = parseUIContext(
        persistedDataSelector(
          getGlobalStore().getState(),
          'liveHelperUiContext',
        ),
      )
      // zustand 5 dropped the subscribe(selector, listener) overload; diff
      // the selected slice manually instead.
      let prevUIContextStr = persistedDataSelector(
        getGlobalStore().getState(),
        'liveHelperUiContext',
      )
      getGlobalStore().subscribe((state: IGlobalState) => {
        const uiContextStr = persistedDataSelector(state, 'liveHelperUiContext')
        if (uiContextStr !== prevUIContextStr) {
          prevUIContextStr = uiContextStr
          persistedUIContextCache = parseUIContext(uiContextStr)
        }
      })
    }
  } catch (e) {
    persistedUIContextCache = initialPersistedUIContext
  }
  return persistedUIContextCache
}

/** Sets the widget state in UI context. */
export function setWidgetState(value: WidgetState) {
  if (value === getLiveHelperUIContext().widgetState) {
    return
  }
  const prevUIContext = getLiveHelperUIContext()
  persistedUIContextCache = {
    ...prevUIContext,
    prevWidgetState: prevUIContext.widgetState,
    widgetState: value,
  }
  sharedStateActionsSelector(getGlobalStore().getState()).setPersistedData(
    'liveHelperUiContext',
    JSON.stringify(persistedUIContextCache),
  )
}

export type UIEvent =
  | { type: 'xstate.init' }
  | { type: 'REQUEST_MICROPHONE_ACCESS' }
  | { type: 'REQUEST_VISITOR_INFORMATION' }
  | {
      type: 'DIAL'
      isTransferredConversation?: boolean
    }
  | { type: 'START_CALL' }
  | { type: 'RECEIVE_CALL' }
  | { type: 'TERMINATE_CALL' }
  | { type: 'EXPAND' }
  | { type: 'MINIMIZE' }
  | { type: 'START_CHAT' }
  | {
      type: 'END_CONVERSATION'
      isActiveConversation?: boolean
      isAppointment?: boolean
    }
  | { type: 'TOGGLE_CHAT' }
  | { type: 'AUTO_START_CHAT' }
  | { type: 'AUTO_DIAL'; isAppointment?: boolean }
  | { type: 'AUTO_START_CALL' }
  | { type: 'AUTO_IDLE' }
  | { type: 'START_CUSTOMER_SURVEY' }
  | { type: 'START_SCHEDULING' }
  | { type: 'SHOW_APPOINTMENT' }
  | { type: 'TRANSFERRING' }
  | { type: 'CLOSE_NPS'; isAppointment?: boolean }

type UITypeState =
  | { value: 'idle_minimized'; context: object }
  | { value: 'idle_expanded'; context: object }
  | { value: 'idle_scheduling'; context: object }
  | { value: 'request_microphone_access'; context: object }
  | { value: 'request_visitor_information'; context: object }
  | { value: 'appointment_lobby'; context: object }
  | { value: 'appointment_dialing'; context: object }
  | { value: 'chatting'; context: object }
  | { value: 'chatting_dialing'; context: object }
  | { value: 'chatting_ringing'; context: object }
  | { value: 'chatting_on_call'; context: object }
  | { value: 'dialing_minimized'; context: object }
  | { value: 'dialing_expanded'; context: object }
  | { value: 'on_call_minimized'; context: object }
  | { value: 'on_call_expanded'; context: object }
  | { value: 'on_call_fullscreen'; context: object }
  | { value: 'customer_survey'; context: object }

export type UIState = StateMachine.State<object, UIEvent, UITypeState>

export type UISend = ServiceFrom<typeof uiMachine>['send']

function wasChat() {
  return getLiveHelperUIContext().prevWidgetState === 'chat'
}

function isMinimized() {
  return getLiveHelperUIContext().widgetState === 'minimized'
}

function isChat() {
  return getLiveHelperUIContext().widgetState === 'chat'
}

function isFullscreen() {
  return getLiveHelperUIContext().widgetState === 'fullscreen'
}

// On Mobile we want to transition to video fullscreen when starting a call
function shouldTransitionToVideoCallFullScreen() {
  return isMobileWeb()
}

const startChatCallTransition = [
  {
    target: 'on_call_minimized',
    cond: () => isMinimized(),
  },
  {
    target: 'on_call_fullscreen',
    cond: shouldTransitionToVideoCallFullScreen,
  },
  {
    target: 'chatting_on_call',
  },
]

const startCallTransition = [
  {
    target: 'on_call_fullscreen',
    cond: shouldTransitionToVideoCallFullScreen,
  },
  {
    target: 'on_call_expanded',
  },
]

const endConversationTransition = [
  {
    target: 'customer_survey',
    cond: (_context, event) =>
      event.isAppointment || event.isActiveConversation,
  },
  {
    target: 'idle_minimized',
  },
]

const dialTransition = [
  {
    target: 'dialing_expanded',
    cond: (_context, event) => event.isTransferredConversation,
  },
  {
    target: 'chatting_dialing',
  },
]

const closeNPSTransition = [
  {
    target: 'appointment_lobby',
    cond: (_context, event) => event.isAppointment,
  },
  {
    target: 'idle_minimized',
  },
]

export const uiMachine = createMachine<object, UIEvent, UITypeState>(
  {
    id: 'live-helper-ui',
    initial: 'idle_minimized',
    states: {
      idle_minimized: {
        entry: ['minimize'],
        on: {
          EXPAND: 'idle_expanded',
          RECEIVE_CALL: 'chatting_ringing',
          START_CHAT: 'chatting',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          END_CONVERSATION: endConversationTransition,
          AUTO_DIAL: [
            {
              target: 'appointment_dialing',
              cond: (_context, event) => !!event.isAppointment,
            },
            { target: 'chatting_dialing', cond: isChat },
            { target: 'dialing_minimized', cond: isMinimized },
          ],
          AUTO_START_CALL: [
            { target: 'on_call_minimized', cond: isFullscreen },
            { target: 'on_call_minimized', cond: isMinimized },
            { target: 'on_call_minimized', cond: isChat },
          ],
          AUTO_START_CHAT: [{ target: 'chatting', cond: isChat }],
          AUTO_IDLE: 'idle_minimized',
          SHOW_APPOINTMENT: 'appointment_lobby',
        },
      },
      idle_expanded: {
        entry: ['minimize'],
        on: {
          MINIMIZE: 'idle_minimized',
          DIAL: 'dialing_expanded',
          RECEIVE_CALL: 'chatting_ringing',
          START_CHAT: 'chatting',
          START_SCHEDULING: 'idle_scheduling',
          REQUEST_MICROPHONE_ACCESS: 'request_microphone_access',
          REQUEST_VISITOR_INFORMATION: 'request_visitor_information',
        },
      },
      idle_scheduling: {
        entry: ['idle_expanded'],
        on: {
          MINIMIZE: 'idle_minimized',
        },
      },
      request_microphone_access: {
        entry: ['idle_expanded', 'request_visitor_information'],
        on: {
          MINIMIZE: 'idle_minimized',
          DIAL: 'dialing_expanded',
        },
      },
      request_visitor_information: {
        entry: ['idle_expanded'],
        on: {
          MINIMIZE: 'idle_minimized',
          REQUEST_MICROPHONE_ACCESS: 'request_microphone_access',
          DIAL: 'dialing_expanded',
        },
      },
      appointment_lobby: {
        entry: ['idle_minimized', 'customer_survey'],
        on: {
          DIAL: 'appointment_dialing',
        },
      },
      appointment_dialing: {
        entry: ['appointment_lobby', 'idle_minimized'],
        on: {
          // TODO(ZE-493): Fix ts issues with state machine

          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          START_CALL: startCallTransition,
          TERMINATE_CALL: 'appointment_lobby',
        },
      },
      chatting: {
        entry: ['chat'],
        on: {
          REQUEST_VISITOR_INFORMATION: 'request_visitor_information',
          REQUEST_MICROPHONE_ACCESS: 'request_microphone_access',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          DIAL: dialTransition,
          RECEIVE_CALL: 'chatting_ringing',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          END_CONVERSATION: endConversationTransition,
          MINIMIZE: 'idle_minimized',
        },
      },
      chatting_dialing: {
        entry: ['chat'],
        on: {
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          START_CALL: startChatCallTransition,
          TERMINATE_CALL: 'chatting',
          MINIMIZE: 'dialing_minimized',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          END_CONVERSATION: endConversationTransition,
        },
      },
      chatting_ringing: {
        entry: ['chat'],
        on: {
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          START_CALL: startChatCallTransition,
          TERMINATE_CALL: 'chatting',
        },
      },
      chatting_on_call: {
        entry: ['chat'],
        on: {
          TERMINATE_CALL: 'chatting',
          MINIMIZE: 'on_call_minimized',
          EXPAND: 'on_call_fullscreen',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          TOGGLE_CHAT: startCallTransition,
        },
      },
      dialing_minimized: {
        entry: ['minimize'],
        on: {
          START_CHAT: 'chatting_dialing',
          TERMINATE_CALL: 'chatting',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          START_CALL: startCallTransition,
          EXPAND: 'dialing_expanded',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          END_CONVERSATION: endConversationTransition,
        },
      },
      dialing_expanded: {
        entry: ['minimize'],
        on: {
          START_CHAT: 'chatting_dialing',
          TERMINATE_CALL: 'idle_minimized',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          START_CALL: startCallTransition,
          MINIMIZE: 'dialing_minimized',
          TOGGLE_CHAT: 'chatting_dialing',
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          END_CONVERSATION: endConversationTransition,
        },
      },
      on_call_minimized: {
        entry: ['minimize'],
        on: {
          START_CHAT: 'chatting_on_call',
          TERMINATE_CALL: 'chatting',
          EXPAND: [
            {
              target: 'chatting_on_call',
              cond: wasChat,
            },
            {
              target: 'on_call_expanded',
              cond: () => !isMobileWeb() && !wasChat(),
            },
            {
              target: 'on_call_fullscreen',
              cond: () => isMobileWeb() && !wasChat(),
            },
          ],
        },
      },
      on_call_expanded: {
        entry: ['minimize'],
        on: {
          TERMINATE_CALL: 'chatting',
          START_CHAT: 'chatting_on_call',
          EXPAND: 'on_call_fullscreen',
          MINIMIZE: 'on_call_minimized',
          TOGGLE_CHAT: 'chatting_on_call',
        },
      },
      on_call_fullscreen: {
        entry: ['fullscreen'],
        on: {
          TERMINATE_CALL: 'chatting',
          MINIMIZE: 'on_call_minimized',
          TOGGLE_CHAT: 'chatting_on_call',
        },
      },
      customer_survey: {
        entry: ['chat'],
        on: {
          // @ts-expect-error TODO(ZE-493): Fix ts issues with state machine
          CLOSE_NPS: closeNPSTransition,
        },
      },
    },
  },
  {
    actions: {
      minimize: assign((context, event) => {
        if (event.type === 'xstate.init') {
          return context
        }
        setWidgetState('minimized')
        return context
      }),
      chat: assign((context, event) => {
        if (event.type === 'xstate.init') {
          return context
        }
        setWidgetState('chat')
        return context
      }),
      fullscreen: assign((context, event) => {
        if (event.type === 'xstate.init') {
          return context
        }
        setWidgetState('fullscreen')
        return context
      }),
    },
  },
)

/** Resets local cache. TEST ONLY. */
export function reset() {
  persistedUIContextCache = undefined
}
